/**
 * What the Studio shows to an account that has not bought the Studio plan.
 *
 * The Studio is OTO 1 and has been a purchasable product since the prototype — the
 * entitlement flag existed, the padlock convention existed, the product row existed,
 * and nothing read any of it. Every "Open in Studio" opened the Studio. This is the
 * screen that stands in its place instead.
 *
 * Presentation only, exactly like LicenseLockedState next door: it takes a callback
 * and renders. The decision about whether to render it at all belongs to the route
 * gate in src/router.tsx, which is the one place that reads the entitlement, and the
 * purchase belongs to the screen the callback navigates to. A component that checked
 * the flag itself would be a second gate to keep in step with the first.
 *
 * NOT A SECURITY BOUNDARY — the entitlement it reflects is a client-side boolean and
 * editable from devtools. The real refusal has to come from the server once the Studio
 * actions call one; see the header of src/stores/entitlementsStore.ts. What this does
 * honestly deliver is that no UI path in the app opens the workspace without the plan.
 */
import { Disc3 } from 'lucide-react';
import { Btn, SectionHeading, UpgradePrompt } from '../ui';
import { formatUpgradeOfferPrice, upgradeOffer } from '../../data/upgradeOffers';
import { c } from '../../styles/theme';

/**
 * The redesigned Studio offer, shared with /products so locked pricing cannot drift.
 */
const STUDIO_OFFER = upgradeOffer('studio-card');

export interface StudioLockedStateProps {
  /** Sends the visitor to the screen that sells the Studio. */
  onGetStudio: () => void;
}

export default function StudioLockedState({ onGetStudio }: StudioLockedStateProps) {
  return (
    <div>
      <SectionHeading
        as="h1"
        icon={<Disc3 size={17} color={c.accent} />}
        title="Lyrixsa Studio"
        subtitle="The production workspace for songs you have already generated."
        marginBottom={18}
      />
      <UpgradePrompt
        paddingY={28}
        icon={<Disc3 size={28} color={c.text3} />}
        title="Studio access requires the Studio plan"
        /* Names what the plan unlocks rather than only what is being withheld, and says
           plainly that the rest of the account is unaffected — the Studio is one of six
           independent one-time products, not a tier that switches the app off. */
        description="Extend, remix, replace sections, split stems, trim, generate cover art and browse version history. Creating, playing, downloading and organising your songs are not affected."
        action={
          <Btn onClick={onGetStudio}>
            <Disc3 size={13} />
            Get {STUDIO_OFFER?.product.name} — {formatUpgradeOfferPrice(STUDIO_OFFER?.product ?? { price: 197 })}
          </Btn>
        }
      />
    </div>
  );
}
